/**
 * PATH: packages/ui/src/components/TabBar.tsx
 * PURPOSE: Browser-style tab bar for multi-page navigation within a hub.
 *
 * WHY: Users need to open multiple views simultaneously (reports, quotes,
 *      customers) and switch between them without losing context — like
 *      browser tabs but inside the app shell.
 *
 * FLOW:
 *  ┌──────────────────────────────────────────────────────────┐
 *  │ [📊 Dashboard] [📝 Quotes ×] [👥 Customer #42 ×] [+]   │
 *  └──────────────────────────────────────────────────────────┘
 *   Active tab ───────────▲
 *
 * DEPENDENCIES:
 *  - lucide-react: X, Plus, Pin, PinOff icons
 *  - cn utility: class merging
 */

import {
  useState,
  useRef,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  type MouseEvent as ReactMouseEvent,
  type ComponentType,
  type ReactNode,
} from 'react'
import { X, Plus, Pin, PinOff, Copy, XCircle, History } from 'lucide-react'
import { cn } from '../lib/utils'

// ─────────────────────────────────────────────────────────────
// Reorder animation tuning (Phase 1 — delayed + animated MRU reorder)
// ─────────────────────────────────────────────────────────────
/** How long the just-clicked tab stays put before sliding to the front. */
const REORDER_DELAY_MS = 2000
/** Duration of the horizontal FLIP slide once the reorder is applied. */
const REORDER_SLIDE_MS = 320

/** Reads the OS "reduce motion" preference so we can skip the slide/delay. */
function usePrefersReducedMotion(): boolean {
  const [reduced, setReduced] = useState(false)
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)')
    setReduced(mq.matches)
    const onChange = () => setReduced(mq.matches)
    mq.addEventListener?.('change', onChange)
    return () => mq.removeEventListener?.('change', onChange)
  }, [])
  return reduced
}

// ─────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────

export interface TabItem {
  /** Unique tab ID */
  id: string
  /** Route path this tab represents */
  path: string
  /** Display title */
  title: string
  /** Optional icon component */
  icon?: ComponentType<{ className?: string }>
  /** Pinned tabs can't be closed and stay on the left */
  isPinned: boolean
  /** Whether the tab has unsaved changes */
  isDirty?: boolean
  /**
   * Email-style "draft" tab — renders with a soft amber tint so estimators
   * can spot a `dr-…` quote draft at a glance. The TabBar treats this
   * purely as a visual signal; promotion logic lives in the consumer
   * (Quote Builder calls `setDraft(tabId, false)` once the draft is
   * promoted to a real Quote).
   */
  isDraft?: boolean
  /** Hub color dot class (e.g., 'bg-teal-400') — for cross-hub tabs */
  hubDotColor?: string
  /** Hub label (e.g., 'Workforce') — shown as subtitle for cross-hub tabs */
  hubLabel?: string
}

/**
 * Modifier-key info forwarded to `onTabClose` so the parent can layer
 * extra UX onto a single close gesture (used today by the SAM tab
 * stores to gate close-on-draft behind a confirm dialog with a
 * "Shift to never ask again" escape hatch).
 */
export interface TabCloseOptions {
  shiftKey?: boolean
  metaKey?: boolean
  ctrlKey?: boolean
  altKey?: boolean
}

export interface TabBarProps {
  /** List of open tabs */
  tabs: TabItem[]
  /** Currently active tab ID */
  activeTabId: string | null
  /** Called when a tab is clicked */
  onTabClick: (tabId: string) => void
  /**
   * Called when a tab close button is clicked. Optional second arg
   * carries modifier-key info from the click event so consumers can
   * branch on Shift/Meta/etc. without re-binding event handlers.
   */
  onTabClose: (tabId: string, options?: TabCloseOptions) => void
  /** Called when the "new tab" button is clicked */
  onNewTab?: () => void
  /** Called to pin/unpin a tab */
  onTabPin?: (tabId: string) => void
  /** Called to close all tabs except the given one */
  onCloseOthers?: (tabId: string) => void
  /** Called to duplicate a tab */
  onDuplicateTab?: (tabId: string) => void
  /** Accent color class for the active tab indicator */
  accentColor?: string
  /** Custom className */
  className?: string
  /**
   * Optional node rendered INSIDE the scrolling tab track, immediately after
   * the last tab. Use this for tab-adjacent affordances that should sit beside
   * the tabs (and scroll with them) rather than being pushed to the far right
   * like the `+` new-tab button. SAM uses it for the "Quote Builder – Recent"
   * quick-jump so it reads as a tab next to "Quote Builder". Optional +
   * defaults to nothing → fully backward compatible for every other consumer.
   */
  trailingTabSlot?: ReactNode
  /**
   * Jump back to the previously activated open tab without reshuffling the
   * strip (operator 2026-07-21). Rendered immediately left of the leading
   * home/pinned tab. Optional — tenants that omit it keep prior chrome.
   */
  onReturnToLastTab?: () => void
  /** Tooltip / title for the return control. */
  returnToLastTabTitle?: string
  /** When false, the control stays visible but disabled. Default true if handler set. */
  returnToLastTabEnabled?: boolean
  /**
   * FEAT-2026-07-03-HEADER-INLINE-TABS — visual variant.
   *
   * 'strip' (default): the standalone browser-tab strip — its own h-9 row
   * with bg-muted, bottom border, bottom-aligned tabs with the rounded-top
   * silhouette. Byte-identical to the pre-variant rendering.
   *
   * 'inline': a chrome-less track designed to sit INSIDE the app header row
   * (operator: breadcrumbs + tabs "should overlay in the same line ... make
   * the screen wider"). No own background/border/height — the host row owns
   * those; tabs centre-align and render as compact pills (active = raised
   * ring) with a bottom accent bar instead of the top ribbon + border trick.
   * Same behaviour everywhere else (MRU reorder, FLIP, context menu, close).
   */
  variant?: 'strip' | 'inline'
}

// ─────────────────────────────────────────────────────────────
// Context Menu
// ─────────────────────────────────────────────────────────────

interface ContextMenuState {
  isOpen: boolean
  tabId: string | null
  position: { x: number; y: number }
}

function TabContextMenu({
  state,
  tab,
  onClose,
  onPin,
  onCloseTab,
  onCloseOthers,
  onDuplicate,
}: {
  state: ContextMenuState
  tab: TabItem | null
  onClose: () => void
  onPin?: (tabId: string) => void
  onCloseTab: (tabId: string) => void
  onCloseOthers?: (tabId: string) => void
  onDuplicate?: (tabId: string) => void
}) {
  const menuRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!state.isOpen) return
    const handleClickOutside = (e: globalThis.MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        onClose()
      }
    }
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('mousedown', handleClickOutside)
    document.addEventListener('keydown', handleEscape)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
      document.removeEventListener('keydown', handleEscape)
    }
  }, [state.isOpen, onClose])

  if (!state.isOpen || !tab) return null

  const items = [
    onPin && {
      label: tab.isPinned ? 'Unpin Tab' : 'Pin Tab',
      icon: tab.isPinned ? PinOff : Pin,
      action: () => { onPin(tab.id); onClose() },
    },
    onDuplicate && {
      label: 'Duplicate Tab',
      icon: Copy,
      action: () => { onDuplicate(tab.id); onClose() },
    },
    !tab.isPinned && {
      label: 'Close Tab',
      icon: X,
      action: () => { onCloseTab(tab.id); onClose() },
    },
    onCloseOthers && {
      label: 'Close Other Tabs',
      icon: XCircle,
      action: () => { onCloseOthers(tab.id); onClose() },
    },
  ].filter(Boolean) as { label: string; icon: ComponentType<{ className?: string }>; action: () => void }[]

  return (
    <div
      ref={menuRef}
      className="fixed z-50 bg-popover border border-border rounded-lg shadow-xl py-1 min-w-[160px] animate-in fade-in-0 zoom-in-95 duration-100"
      style={{ top: state.position.y, left: state.position.x }}
    >
      {items.map((item) => {
        const ItemIcon = item.icon
        return (
          <button
            key={item.label}
            onClick={item.action}
            className="w-full flex items-center gap-2 px-3 py-1.5 text-[12px] text-popover-foreground hover:bg-muted transition-colors text-left"
          >
            <ItemIcon className="w-3.5 h-3.5 text-slate-400" />
            <span>{item.label}</span>
          </button>
        )
      })}
    </div>
  )
}

// ─────────────────────────────────────────────────────────────
// TabBar Component
// ─────────────────────────────────────────────────────────────

export function TabBar({
  tabs,
  activeTabId,
  onTabClick,
  onTabClose,
  onNewTab,
  onTabPin,
  onCloseOthers,
  onDuplicateTab,
  accentColor = 'bg-teal-500',
  className,
  trailingTabSlot,
  onReturnToLastTab,
  returnToLastTabTitle = 'Return to last opened tab',
  returnToLastTabEnabled = true,
  variant = 'strip',
}: TabBarProps) {
  const inline = variant === 'inline'
  const scrollRef = useRef<HTMLDivElement>(null)
  const [contextMenu, setContextMenu] = useState<ContextMenuState>({
    isOpen: false,
    tabId: null,
    position: { x: 0, y: 0 },
  })

  // ── Phase 1: delayed + animated most-recently-used reorder ──────────────
  // The parent hands us `tabs` already ordered most-recently-used-first, so a
  // click makes the active tab snap to the front on the very next render —
  // "too fast and visually confusing" (operator). We keep the ACTIVE highlight
  // instant (it's driven by `activeTabId`, untouched here) but hold the visual
  // RE-ORDER for a beat, then let the FLIP effect below slide the tab into
  // place. Only a pure reorder of the SAME set is delayed; adding/removing a
  // tab (or reduced-motion) applies immediately. Purely presentational — no
  // store or navigation logic changes.
  const prefersReducedMotion = usePrefersReducedMotion()
  const [displayOrder, setDisplayOrder] = useState<string[]>(() => tabs.map(t => t.id))
  const displayOrderRef = useRef<string[]>(displayOrder)
  useEffect(() => { displayOrderRef.current = displayOrder }, [displayOrder])
  const reorderTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)

  useEffect(() => {
    const incoming = tabs.map(t => t.id)
    const current = displayOrderRef.current
    const sameOrder =
      current.length === incoming.length && current.every((id, i) => id === incoming[i])
    if (sameOrder) return
    const setKey = (ids: string[]) => ids.slice().sort().join('\u0000')
    const sameSet = current.length === incoming.length && setKey(current) === setKey(incoming)
    clearTimeout(reorderTimer.current)
    if (!sameSet || prefersReducedMotion) {
      setDisplayOrder(incoming) // add/remove (or reduced-motion) → apply now
      return
    }
    // Same tabs, new order → hold, then apply (the FLIP effect animates it).
    reorderTimer.current = setTimeout(() => setDisplayOrder(incoming), REORDER_DELAY_MS)
    return () => clearTimeout(reorderTimer.current)
  }, [tabs, prefersReducedMotion])

  // Resolve the render order from `displayOrder` (appending any brand-new id
  // not yet tracked), then keep pinned tabs anchored at the front (stable).
  const sortedTabs = useMemo(() => {
    const byId = new Map(tabs.map(t => [t.id, t]))
    const seen = new Set<string>()
    const out: TabItem[] = []
    for (const id of displayOrder) {
      const t = byId.get(id)
      if (t) { out.push(t); seen.add(id) }
    }
    for (const t of tabs) if (!seen.has(t.id)) out.push(t)
    return out.sort((a, b) => {
      if (a.isPinned && !b.isPinned) return -1
      if (!a.isPinned && b.isPinned) return 1
      return 0
    })
  }, [tabs, displayOrder])

  // FLIP: once the render order changes, slide each tab from its previous
  // x-position to the new one (transform only — no remount, no scroll jump).
  const tabElRefs = useRef<Map<string, HTMLDivElement>>(new Map())
  const prevLefts = useRef<Map<string, number>>(new Map())
  useLayoutEffect(() => {
    const next = new Map<string, number>()
    tabElRefs.current.forEach((el, id) => {
      if (!el) return
      const left = el.offsetLeft
      next.set(id, left)
      if (prefersReducedMotion) return
      const prev = prevLefts.current.get(id)
      if (prev != null && prev !== left) {
        const dx = prev - left
        el.style.transition = 'none'
        el.style.transform = `translateX(${dx}px)`
        requestAnimationFrame(() => {
          el.style.transition = `transform ${REORDER_SLIDE_MS}ms ease`
          el.style.transform = ''
        })
      }
    })
    prevLefts.current = next
  }, [sortedTabs, prefersReducedMotion])

  const handleContextMenu = useCallback((e: ReactMouseEvent, tabId: string) => {
    e.preventDefault()
    setContextMenu({ isOpen: true, tabId, position: { x: e.clientX, y: e.clientY } })
  }, [])

  const handleMiddleClick = useCallback((e: ReactMouseEvent, tabId: string) => {
    // Middle mouse button = close tab (browser convention)
    if (e.button === 1) {
      e.preventDefault()
      const tab = tabs.find(t => t.id === tabId)
      if (tab && !tab.isPinned) {
        onTabClose(tabId)
      }
    }
  }, [tabs, onTabClose])

  // Scroll active tab into view when it changes
  useEffect(() => {
    if (!activeTabId || !scrollRef.current) return
    const activeEl = scrollRef.current.querySelector(`[data-tab-id="${activeTabId}"]`)
    if (activeEl) {
      activeEl.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' })
    }
  }, [activeTabId])

  const contextTab = contextMenu.tabId ? tabs.find(t => t.id === contextMenu.tabId) ?? null : null

  if (tabs.length === 0) return null

  return (
    <>
      {/*
        Visual model (shrunk 2026-05-04 after jake's "Why the fuck did
        you make it so big now for the tabs.. reduce the text and
        height" feedback):
          - Strip drops from h-10 → h-8 (32px). Tabs sit at h-7 (28px)
            with text-[11px] and px-2 padding — same compact density
            as the previous Bravada/Workforce strips.
          - Active tab keeps the bg-background + side borders + 2px
            primary ribbon "browser tab" silhouette, but everything
            scales down so it doesn't dominate the chrome.
          - Pin icon is now w-2.5/h-2.5 + 50% opacity so it reads as
            a small status dot, not a logo.
      */}
      {/*
          MOBILE-KANBAN-FIRST-PRINCIPLES-ARC §3.1 (Phase 5) — the
          browser-style hub tab strip is a desktop affordance. On
          phone (<md) the MobileDock is the canonical nav surface,
          and rendering a SECOND horizontally-scrollable nav strip
          here was the source of the "Quote Builder → ilder /
          Invoicing → Invoic" label-truncation operator complaint
          (screenshot 2026-05-17 §). The strip is now `hidden
          md:flex`: phone uses the dock, tablet/desktop keep the
          browser-tabs metaphor. We did NOT delete the component —
          tablet (768-1023 px) still benefits from the tab strip
          and many embeds (CFO, Workforce, Bravada) render on
          tablet routinely.
      */}
      <div
        className={cn(
          inline
            ? // Inline variant — the host header row owns height/background/
              // border; the track just centres its pills and shrinks politely.
              'hidden md:flex items-center min-w-0 flex-1 gap-0.5 select-none'
            : // h-9 (36px) over the prior h-8 (32px): with `items-end` the
              // 28px tabs bottom-align, so the extra 4px becomes top
              // headroom. At h-8 the tabs' rounded tops + the active-tab
              // accent ribbon (`top-0`) sat flush against the element above
              // and read as "cut off from the top" (operator 2026-06-10).
              // Still well short of the old h-10 the "reduce the height"
              // feedback removed.
              'hidden md:flex items-end h-9 bg-muted/60 border-b border-border px-1.5 gap-0.5 select-none flex-shrink-0',
          className
        )}
      >
        {/* Tab scroll container */}
        <div
          ref={scrollRef}
          className={cn(
            'flex-1 flex overflow-x-auto scrollbar-none gap-0.5',
            inline ? 'items-center' : 'items-end'
          )}
        >
          {onReturnToLastTab && (
            <div className={cn('flex h-full flex-shrink-0', inline ? 'items-center' : 'items-end')}>
              <button
                type="button"
                data-tab-action="return-last"
                onClick={onReturnToLastTab}
                disabled={!returnToLastTabEnabled}
                title={returnToLastTabTitle}
                aria-label={returnToLastTabTitle}
                className={cn(
                  'group relative flex items-center justify-center h-7 w-7 text-[11px] font-medium transition-all',
                  inline ? 'rounded-md' : 'rounded-t-md',
                  returnToLastTabEnabled
                    ? inline
                      ? 'text-muted-foreground hover:bg-muted/70 hover:text-foreground'
                      : 'text-muted-foreground hover:bg-background/70 hover:text-foreground'
                    : 'text-muted-foreground/40 cursor-not-allowed',
                )}
              >
                <History className="w-3.5 h-3.5" />
              </button>
            </div>
          )}
          {sortedTabs.map((tab) => {
            const isActive = tab.id === activeTabId
            const TabIcon = tab.icon

            return (
              <div
                key={tab.id}
                ref={(el) => {
                  if (el) tabElRefs.current.set(tab.id, el)
                  else tabElRefs.current.delete(tab.id)
                }}
                className={cn('flex h-full', inline ? 'items-center' : 'items-end')}
              >
                <button
                  data-tab-id={tab.id}
                  onClick={() => onTabClick(tab.id)}
                  onMouseDown={(e) => handleMiddleClick(e, tab.id)}
                  onContextMenu={(e) => handleContextMenu(e, tab.id)}
                  className={cn(
                    'group relative flex items-center gap-1 h-7 px-2 text-[11px] font-medium whitespace-nowrap transition-all min-w-0',
                    inline ? 'rounded-md' : 'rounded-t-md',
                    tab.isPinned ? 'max-w-[150px]' : 'max-w-[190px]',
                    // Draft tabs get a soft amber tint regardless of
                    // active state. The hover/active variants keep the
                    // same yellow family so the tab still reads as
                    // "draft" when it's the focused workbook.
                    tab.isDraft
                      ? isActive
                        ? inline
                          ? 'bg-amber-50 text-amber-900 ring-1 ring-amber-300/70 shadow-sm dark:bg-amber-950/30 dark:text-amber-100 dark:ring-amber-700/60'
                          : 'bg-amber-50 text-amber-900 border-t border-l border-r border-amber-300/70 -mb-px pb-px dark:bg-amber-950/30 dark:text-amber-100 dark:border-amber-700/60'
                        : 'bg-amber-50/60 text-amber-800 hover:bg-amber-100/70 dark:bg-amber-950/20 dark:text-amber-200 dark:hover:bg-amber-900/30'
                      : isActive
                        ? inline
                          ? 'bg-muted text-foreground ring-1 ring-border shadow-sm'
                          : 'bg-background text-foreground border-t border-l border-r border-border -mb-px pb-px'
                        : inline
                          ? 'text-muted-foreground hover:bg-muted/70 hover:text-foreground'
                          : 'text-muted-foreground hover:bg-background/70 hover:text-foreground'
                  )}
                  title={tab.title}
                >
                  {/* Active indicator — accent ribbon. 2px to match the
                      compact tab height; rounded ends keep the chrome
                      feeling intentional, not stamped on. Draft tabs use an
                      amber ribbon to reinforce the yellow-tint signal.
                      Strip: top edge (browser-tab silhouette). Inline:
                      bottom edge (underline inside the pill). */}
                  {isActive && (
                    <div className={cn(
                      'absolute left-1.5 right-1.5 h-[2px] rounded-full',
                      inline ? 'bottom-0' : 'top-0',
                      tab.isDraft ? 'bg-amber-500' : (tab.hubDotColor ? tab.hubDotColor : accentColor),
                    )} />
                  )}

                  {/* Hub color dot — shown when tabs span multiple hubs */}
                  {tab.hubDotColor && (
                    <span className={cn('w-1.5 h-1.5 rounded-full flex-shrink-0', tab.hubDotColor)} />
                  )}

                  {/* Pin icon (tiny status dot) */}
                  {tab.isPinned && !tab.hubDotColor && (
                    <Pin className={cn('w-2.5 h-2.5 flex-shrink-0 -rotate-45', isActive ? 'text-foreground/50' : 'text-muted-foreground/50')} />
                  )}

                  {/* Tab icon (for unpinned tabs without hub dot) */}
                  {TabIcon && !tab.isPinned && !tab.hubDotColor && (
                    <TabIcon className={cn(
                      'w-3 h-3 flex-shrink-0',
                      isActive ? 'text-foreground' : 'text-muted-foreground/80'
                    )} />
                  )}

                  {/* Title + optional hub subtitle */}
                  <span className="truncate flex flex-col leading-tight">
                    <span className="truncate">{tab.title}</span>
                    {tab.hubLabel && (
                      <span className="text-[8px] font-normal text-muted-foreground truncate -mt-0.5">{tab.hubLabel}</span>
                    )}
                  </span>

                  {/* Dirty indicator (unsaved changes) */}
                  {tab.isDirty && (
                    <span className="w-1.5 h-1.5 rounded-full bg-amber-400 flex-shrink-0" />
                  )}

                  {/* Close affordance — rendered as a focusable span
                      (role="button") because the outer tab is already
                      a <button> and HTML forbids nested buttons (React
                      hydration warning: "In HTML, <button> cannot be a
                      descendant of <button>"). */}
                  {!tab.isPinned && (
                    <span
                      role="button"
                      tabIndex={-1}
                      onClick={(e) => {
                        e.stopPropagation()
                        onTabClose(tab.id, {
                          shiftKey: e.shiftKey,
                          metaKey: e.metaKey,
                          ctrlKey: e.ctrlKey,
                          altKey: e.altKey,
                        })
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault()
                          e.stopPropagation()
                          onTabClose(tab.id, {
                            shiftKey: e.shiftKey,
                            metaKey: e.metaKey,
                            ctrlKey: e.ctrlKey,
                            altKey: e.altKey,
                          })
                        }
                      }}
                      aria-label={`Close ${tab.title}`}
                      className={cn(
                        'flex items-center justify-center w-3.5 h-3.5 rounded-sm flex-shrink-0 transition-colors -mr-0.5 cursor-pointer',
                        isActive
                          ? 'text-muted-foreground hover:bg-muted hover:text-foreground'
                          : 'opacity-0 text-muted-foreground hover:bg-muted hover:text-foreground group-hover:opacity-70 hover:!opacity-100'
                      )}
                    >
                      <X className="w-2.5 h-2.5" />
                    </span>
                  )}
                </button>
              </div>
            )
          })}

          {/* Tab-adjacent affordance (scrolls with the tabs, sits beside the
              last one). Empty for every consumer that doesn't pass it. */}
          {trailingTabSlot}
        </div>

        {/* New tab button */}
        {onNewTab && (
          <button
            onClick={onNewTab}
            className={cn(
              'flex items-center justify-center w-6 h-6 rounded-md text-muted-foreground hover:text-foreground transition-colors flex-shrink-0 ml-0.5',
              inline ? 'hover:bg-muted/70' : 'mb-0.5 hover:bg-background/70'
            )}
            title="New tab (Ctrl+T)"
          >
            <Plus className="w-3 h-3" />
          </button>
        )}
      </div>

      {/* Context menu (portal-free for simplicity) */}
      <TabContextMenu
        state={contextMenu}
        tab={contextTab}
        onClose={() => setContextMenu(prev => ({ ...prev, isOpen: false }))}
        onPin={onTabPin}
        onCloseTab={onTabClose}
        onCloseOthers={onCloseOthers}
        onDuplicate={onDuplicateTab}
      />
    </>
  )
}

export default TabBar
